/**
 * Override Policy Modal — Doc 17 §5
 *
 * Shown when plan_constraint_evaluations has severe (fail) violations.
 * Calls createGovernanceOverrideForViolations and records a tradeoff_decision.
 */

import { useState } from 'react';
import { ShieldAlert, X, Loader2 } from 'lucide-react';
import { createGovernanceOverrideForViolations } from '../../lib/planningService';
import { isSevereViolation } from '../../lib/constraintViolations';
import type { ConstraintViolation } from '../../lib/constraintViolations';

export interface OverridePolicyModalProps {
  planVersionId: string;
  evaluationId: string;
  violations: ConstraintViolation[];
  onClose: () => void;
  onOverridden: () => void;
}

export function OverridePolicyModal({
  planVersionId,
  evaluationId,
  violations,
  onClose,
  onOverridden,
}: OverridePolicyModalProps) {
  const [rationale, setRationale] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const severeViolations = violations.filter(isSevereViolation);

  const handleSave = async () => {
    if (!rationale.trim()) { setError('Rationale is required.'); return; }
    setSaving(true);
    setError(null);
    const { error: err } = await createGovernanceOverrideForViolations(
      planVersionId,
      evaluationId,
      rationale.trim(),
    );
    setSaving(false);
    if (err) { setError(err); return; }
    onOverridden();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-[var(--coral)]" />
            <h3 className="text-base font-semibold text-[var(--text-pri)]">Override policy violation</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        {/* Violations list */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-[var(--text-sec)]">
            The following {severeViolations.length === 1 ? 'violation' : 'violations'} require an explicit governance decision before this plan can be authorized as Plan of Record.
          </p>

          <div className="space-y-2">
            {severeViolations.map((v, i) => (
              <div key={i} className="px-3 py-2 bg-[var(--coral-dim)] border border-[var(--coral)] space-y-0.5">
                <p className="text-xs font-mono font-medium text-[var(--coral)]">{v.check_key}</p>
                {v.explanation && <p className="text-xs text-[var(--coral)]">{v.explanation}</p>}
              </div>
            ))}
          </div>

          {/* Rationale input */}
          <div>
            <label htmlFor="rationale" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Rationale <span className="text-[var(--coral)]">*</span>
            </label>
            <textarea
              id="rationale"
              rows={3}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain why this plan should proceed despite the violations above…"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">
              This decision is recorded as a tradeoff in the audit trail and attached to the plan version.
            </p>
          </div>

          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !rationale.trim()}
            className="px-4 py-2 bg-[var(--coral)] text-white text-sm font-medium hover:bg-[var(--coral)] disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Record override
          </button>
        </div>
      </div>
    </div>
  );
}
